import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "react-bootstrap/Button";
import Modal from "common/modal";
import RoomView from "./roomView";
import ProductView from "./productView";
import InputField from "common/customInputField";
import Select from "react-select";
import { editProject } from "store/siteRepo/actionCreator";
import editIcon from "svg/edit_blue.svg";
import GenerateSitePdfModal from "../../GenerateSitePdfModal";
import "./style.scss";

const EditFloorPlanNameModal = ({ projectId, initialName, close }) => {
  const dispatch = useDispatch();
  const { sub } = useSelector((state) => state.loggedInUser);
  const [projectName, setProjectName] = useState(initialName);

  useEffect(() => setProjectName(initialName), [initialName]);

  return (
    <Modal
      title="Edit Floor Plan Name"
      handleSave={() => {
        dispatch(editProject(projectId, { projectName, updatedBy: sub }));
        close();
      }}
      handleCancel={close}
      size="lg"
      closeButtonText="Cancel"
      saveButtonText="Save"
    >
      <div className="container-fluid p-2 border-0 m-0">
        <div className="search-form-container__fields">
          <form className="search-form-container__fields__form">
            <div className="row align-items-center">
              <div className="col-12 col-sm-12 col-md-6 col-lg-6 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  id="floorPlanName"
                  label="Floor Plan Name"
                  placeholder={"Enter Floor Plan Name"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  disabled={false}
                  isRequired={false}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

const LayerInfo = ({ changeStage, project, handleShowMore, layer, scrollPosition }) => {
  const dispatch = useDispatch();

  const noteTimerRefs = {
    specialInstructions: useRef(null),
    installerNotes: useRef(null),
    floorPlanNotes: useRef(null),
  };

  const [isShowEditFloorDetails, setIsShowEditFloorDetails] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [editFloorDetailsData, setEditFloorDetailsData] = useState({});
  const [instructionWarningMsg, setInstructionWarningMsg] = useState("");
  const [installerWarningMsg, setInstallerWarningMsg] = useState("");

  const { sub, terrCcn, storeCcn } = useSelector((state) => state.loggedInUser);
  const { viewSite } = useSelector((state) => state.siteRepo);
  const { territory, storeNumber } = viewSite;

  const [floorPlanNotes, setFloorPlanNotes] = useState(project.floorPlanNotes);
  const [specialInstructions, setSpecialInstructions] = useState(project.specialInstructions);
  const [installerNotes, setInstallerNotes] = useState(project.installerNotes);
  const [showGeneratePdfModal, setShowGeneratePdfModal] = useState(false);

  const handleFloorDetailsModal = (isShow) => {
    setIsShowEditFloorDetails(isShow);
    if (isShow) {
      setEditFloorDetailsData({ floorPlanName: project.name });
    } else {
      setEditFloorDetailsData({});
    }
  };

  const handleNotesChange = (e) => {
    const regex = /[^ -~]|[?\/]/g;
    const id = e.target.id;
    let value = e.target.value;
    let warningMsg = "";
    let invalidChars = [];

    if (id === "floorPlanNotes") {
      setFloorPlanNotes(value);
    } else {
      value.split("").map((char) => {
        if (regex.test(char) && !invalidChars.includes(char)) {
          invalidChars.push(char);
        }
      });
      value = value.replaceAll(regex, "");
      if (invalidChars.length) {
        warningMsg = `Character(s) '${invalidChars.join(", ")}' cannot be processed by POS.`;
      }
      if (value.length === 180) {
        warningMsg = "Maximum length of 180 characters has been reached.";
      }
      switch (id) {
        case "specialInstructions":
          setInstructionWarningMsg(warningMsg);
          setSpecialInstructions(value);
          break;
        case "installerNotes":
          setInstallerWarningMsg(warningMsg);
          setInstallerNotes(value);
          break;
      }
    }
    clearTimeout(noteTimerRefs[id].current);
    noteTimerRefs[id].current = setTimeout(() => {
      dispatch(editProject(project.projectId, { [e.target.id]: value === "" ? null : value, updatedBy: sub }));
    }, 1000);
  };

  const handleProjectSelection = (selectedProject) => {
    if (!selectedProject) return;
    viewSite.projectDetailsList.map((project) => {
      if (selectedProject.value === project.projectId) {
        if (project.layerInfoList.length === 1) {
          handleShowMore(project.layerInfoList[0], "layer", project);
        } else {
          handleShowMore({}, "layer", project);
        }
        setFloorPlanNotes(project.floorPlanNotes);
        setSpecialInstructions(project.specialInstructions);
        setInstallerNotes(project.installerNotes);
      }
    });
  };

  const checkOtherUnits = () => {
    window.scrollTo(scrollPosition.scrollX, scrollPosition.scrollY);
    changeStage("project");
  };

  return (
    <div className="layer-info-container">
      <div className="container-fluid search-form-container heading-section d-flex flex-column">
        <div className="layer-container">
          <div className="search-form-container__fields ">
            <div className="d-flex justify-content-between">
              <div className="d-flex justify-content-start">
                <div className="search-form-container__fields__form-group">
                  <label className="select__label">Floor Plan</label>
                  <Select
                    className="projectDropdown"
                    classNamePrefix="projectDropdown"
                    options={(viewSite.projectDetailsList ?? []).map((project) => ({
                      value: project.projectId,
                      label: project.name,
                    }))}
                    isSearchable={false}
                    value={{ value: project.projectId, label: project.name }}
                    onChange={handleProjectSelection}
                  />
                </div>
                {((territory === terrCcn && territory !== "800100") || storeCcn === storeNumber) && (
                  <div className="d-flex">
                    <img
                      src={editIcon}
                      className="edit-floor"
                      alt="..."
                      onClick={() => handleFloorDetailsModal(true)}
                    />
                  </div>
                )}
              </div>
              <div className="d-flex align-items-center">
                <Button
                  variant="outline-primary"
                  className=" d-none d-md-flex"
                  onClick={() => setShowGeneratePdfModal(true)}
                >
                  Generate PDF
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="d-md-flex flex-column flex-md-row mt-5 mt-md-4 overflow-lg-auto position-relative site-date-time-info col-lg-4 col-md-8 justify-content-between">
          <div className="d-flex justify-content-between align-items-center">
            <div className="label-id">Floor Plan ID: </div>&nbsp;
            <div className="label-id">{project.projectId}</div>
          </div>
        </div>

        <div className="d-none d-md-flex flex-column flex-md-row mt-5 mt-md-4 overflow-lg-auto position-relative site-date-time-info col-lg-4 col-md-8 justify-content-between">
          {project.createdBy && project.createdByTimeStamp && (
            <div className="">
              <div className="d-flex justify-content-between align-items-center">
                <div className="label">Created By: </div>
                <div className="value">{project.createdBy}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <div className="label">Created Date: </div>
                <div className="value">{new Date(project.createdByTimeStamp).toLocaleDateString("en-US")}</div>
              </div>
            </div>
          )}
          {project.updatedBy && project.updatedByTimeStamp && (
            <div className="col-5">
              <div className="d-flex justify-content-between align-items-center">
                <div className="label">Updated By: </div>
                <div className="value">{project.updatedBy}</div>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <div className="label">Updated Date: </div>
                <div className="value">{new Date(project.updatedByTimeStamp).toLocaleDateString("en-US")}</div>
              </div>
            </div>
          )}
        </div>

        <div className="container px-0">
          <Button
            variant="outline-primary"
            className="col-1 my-4"
            id="notesBtn"
            data-bs-toggle="collapse"
            data-bs-target="#notesSection"
            onClick={() => setNotesOpen(!notesOpen)}
          >
            {notesOpen ? "Hide Notes" : "Notes"}
          </Button>
          <div
            className="collapse justify-content-center border border-dark-subtle rounded-4 p-5 mb-4"
            id="notesSection"
          >
            <div className="row mb-4">
              <div className="floormap-card col mx-3">
                <div className="floormap-card__heading-container px-1">
                  <span className="floormap-card__floor-name">Special Instructions (Can be loaded into POS)</span>
                </div>
                <textarea
                  className="text-area-floor col-12 p-3"
                  id="specialInstructions"
                  maxLength={180}
                  value={specialInstructions || ""}
                  style={{ margin: instructionWarningMsg.length > 0 ? "0px" : "0px 0px 38px 0px" }}
                  onInput={handleNotesChange}
                />
                <p className={`p-1 ${instructionWarningMsg ? "visible" : "hidden"}`}>{instructionWarningMsg}</p>
              </div>
              <div className="floormap-card col mx-3">
                <div className="floormap-card__heading-container px-1">
                  <span className="floormap-card__floor-name">Installer Notes (Can be loaded into POS)</span>
                </div>
                <textarea
                  className="text-area-floor col-12 p-3"
                  id="installerNotes"
                  maxLength={180}
                  value={installerNotes || ""}
                  style={{ margin: installerWarningMsg.length > 0 ? "0px" : "0px 0px 38px 0px" }}
                  onInput={handleNotesChange}
                />
                <p className={`p-1 ${installerWarningMsg ? "visible" : "hidden"}`}>{installerWarningMsg}</p>
              </div>
            </div>
            <div className="row">
              <div className="floormap-card col mx-3">
                <div className="floormap-card__heading-container px-1">
                  <span className="floormap-card__floor-name">Floor Plan Notes</span>
                </div>
                <textarea
                  className="text-area-floor col-12 p-3 overflow-auto"
                  id="floorPlanNotes"
                  style={{ margin: "0px 0px 53px 0px" }}
                  rows={15}
                  value={floorPlanNotes || ""}
                  onInput={handleNotesChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="layer-info-main-content">
        <ul className="nav nav-tabs" id="layerDetailsViewTab" role="tablist">
          <li className="nav-item" role="presentation">
            <Button
              variant="link"
              className="nav-link rounded-0"
              id="roomview-tab"
              data-bs-toggle="tab"
              data-bs-target="#roomview-element"
              role="tab"
              aria-controls="roomview-elemen"
              aria-selected="true"
            >
              Room View
            </Button>
          </li>
          <li className="nav-item" role="presentation">
            <Button
              variant="link"
              className="nav-link active rounded-0"
              id="productview-tab"
              data-bs-toggle="tab"
              data-bs-target="#productview-element"
              role="tab"
              aria-controls="productview-element"
              aria-selected="false"
            >
              Product View
            </Button>
          </li>
        </ul>
        <div className="tab-content mb-4" id="layerDetailsViewTabContent">
          <div className="tab-pane fade" id="roomview-element" role="tabpanel" aria-labelledby="roomview-tab">
            <RoomView
              project={project}
              territoryAccess={(territory === terrCcn && territory !== "800100") || storeCcn === storeNumber}
            />
          </div>
          <div
            className="tab-pane fade show active"
            id="productview-element"
            role="tabpanel"
            aria-labelledby="productview-tab"
          >
            <ProductView
              project={project}
              layerLevelData={layer}
              territoryAccess={(territory === terrCcn && territory !== "800100") || storeCcn === storeNumber}
            />
          </div>
        </div>
      </div>
      <div className="container-fluid search-form-container footer-actions mb-5 py-4 d-flex flex-column flex-md-row">
        <Button variant="outline-primary" onClick={checkOtherUnits}>
          Check Other Units
        </Button>
      </div>
      {isShowEditFloorDetails && (
        <EditFloorPlanNameModal
          projectId={project.projectId}
          initialName={editFloorDetailsData.floorPlanName}
          close={() => handleFloorDetailsModal(false)}
        />
      )}
      {showGeneratePdfModal && (
        <GenerateSitePdfModal floors={[project]} onClose={() => setShowGeneratePdfModal(false)} />
      )}
    </div>
  );
};

export default LayerInfo;
